import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { usePermission } from '@/hooks/usePermission';

/**
 * 메뉴 권한 기반 라우트 접근 제어.
 *
 * 사용법
 *   <PrivateRoute menuCode={MENU.CRM_CUSTOMER}>...</PrivateRoute>   ← 권장
 *   <PrivateRoute menuId={21}>...</PrivateRoute>                    ← 레거시 호환
 */
const PrivateRoute = ({ children, menuId, menuCode }) => {
  const { canView, isLoading } = usePermission(menuCode ?? menuId);
  const alertedRef = useRef(false);

  /*
   * 한 번 통과한 뒤에는 다시 '확인 중'으로 돌아가더라도 화면을 걷어내지 않는다.
   *
   * 걷어내면 아래 children 이 통째로 다시 마운트되어 페이지 상태가 초기화된다.
   * 열려 있던 팝업·입력하던 내용이 그대로 사라지므로 사용자에게는 "창이 닫혔다"로 보인다.
   * 권한이 실제로 사라졌다면 아래 denied 판정에서 걸러진다.
   */
  const [wasGranted, setWasGranted] = useState(false);
  if (!wasGranted && !isLoading && canView) setWasGranted(true);

  const denied = !isLoading && !canView;

  // 렌더링 도중 alert 를 띄우면 React 경고가 발생하므로 커밋 이후로 미룬다.
  useEffect(() => {
    if (denied && !alertedRef.current) {
      alertedRef.current = true;
      alert('해당 메뉴에 대한 접근 권한이 없습니다.');
    }
  }, [denied]);

  if (isLoading && !wasGranted) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Verifying Access...</p>
        </div>
      </div>
    );
  }

  if (denied) return <Navigate to="/dashboard" replace />;

  return children;
};

export default PrivateRoute;
